import React, { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import ICart from "../../../models/ICart";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetCartDTO from "../../../models/DTO/IOnGetCartDTO";
import CartService from "@services/CartService";
import CartStatusEnum from "../../../models/CartStatusEnum";
import { useCart } from "../../../contexts/CartContext";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import OwTitle from "../../shared/OwTitle/OwTitle";
import styled from "styled-components";
import TitleTagEnum from "../../../models/TitleTagEnum";
import { EcommerceCheckoutShipTo } from "../EcommerceCheckoutShipTo/EcommerceCheckoutShipTo";
import DeliveryDeadlineInfo from "../../shared/DeliveryDeadlineInfo/DeliveryDeadlineInfo";
import IOnUpdateCartShipToDTO from "../../../models/DTO/IOnUpdateCartShipToDTO";
import { EcommerceStripeCardForm } from "../EcommerceStripeCardForm/EcommerceStripeCardForm";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import ICreateCartPaymentIntentDTO from "../../../models/DTO/ICreateCartPaymentIntentDTO";
import IOnCreateCartPaymentIntentDTO from "../../../models/DTO/IOnCreateCartPaymentIntentDTO";
import ICartValidateInventoryDTO from "../../../models/DTO/ICartValidateInventoryDTO";
import ICartOnValidateInventoryDTO from "../../../models/DTO/ICartOnValidateInventoryDTO";
import SocketService from "../../../services/SocketService";
import SupplierService from "../../../services/SupplierService";
import ICart_Item from "../../../models/ICart_Item";
import IUpdateCartItemDTO from "../../../models/DTO/IUpdateCartItemDTO";
import IDeleteCartItemDTO from "../../../models/DTO/IDeleteCartItemDTO";
import IOnDeleteCartItemDTO from "../../../models/DTO/IOnDeleteCartItemDTO";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import IUpdatePaymentIntentDTO from "../../../models/DTO/IUpdatePaymentIntentDTO";
import IOnUpdatePaymentIntentDTO from "../../../models/DTO/IOnUpdatePaymentIntentDTO";
import { EcommerceCheckoutCartItem } from "../EcommerceCheckoutCartItem/EcommerceCheckoutCartItem";
import { EcommerceCartKitEditor } from "../EcommerceCartKitEditor/EcommerceCartKitEditor";
import OwButton from "../../shared/OwButton/OwButton";
import ICartCompleteCheckoutDTO from "../../../models/DTO/ICartCompleteCheckoutDTO";
import ICartOnCompleteCheckoutDTO from "../../../models/DTO/ICartOnCompleteCheckoutDTO";
import { useNavigate } from "react-router-dom";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwRadioSelect from "../../shared/OwRadioSelect/OwRadioSelect";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import IOwRadioSelectOption from "../../../models/IOwRadioSelectOption";
import { Socket } from "socket.io-client";
import ICartOnCalculateTotalsDTO from "../../../models/DTO/ICartOnCalculateTotalsDTO";
import ICartCalculateTotalsDTO from "../../../models/DTO/ICartCalculateTotalsDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IUpdateCartPropDTO from "../../../models/DTO/IUpdateCartPropDTO";
import IOnUpdateCartPropDTO from "../../../models/DTO/IOnUpdateCartPropDTO";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { getCapturedUtm } from "../../../utils/UtmCapture";
import CatalogService from "@services/CatalogService";
import UserService from "@services/UserService";
import PromoCodeService from "@services/PromoCodeService";
import MinimumOrderService from "@services/MinimumOrderService";
import MinOrderTotalNotice from "../../shared/MinOrderTotalNotice/MinOrderTotalNotice";
import { OwPromoCodeInput } from "../../shared/OwPromoCodeInput/OwPromoCodeInput";
import ICartApplyPromoCodeDTO from "../../../models/DTO/ICartApplyPromoCodeDTO";
import ICartRemovePromoCodeDTO from "../../../models/DTO/ICartRemovePromoCodeDTO";
import ICartOnApplyPromoCodeDTO from "../../../models/DTO/ICartOnApplyPromoCodeDTO";

type Props = {};

export const EcommerceCheckout: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const { refreshInactiveCarts } = useCart();

    const history = useNavigate();

    let currencyName = vendorModel.Catalog?.Currency?.name ? vendorModel.Catalog.Currency.name : "USD";
    if (vendorModel.Catalog?.currency_id) {
        currencyName = CatalogService.getCurrencyNameFromId(vendorModel.Catalog.currency_id);
    }

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    // Card checkout runs through Stripe only when the supplier has Stripe enabled. Net-terms buyers, and
    // buyers of a supplier without Stripe, place the order directly (CartCompleteCheckout, no payment).
    const payWithStripe = !!supplierModel.Supplier_Integration.has_stripe && !vendorModel.net_term_ecomm_user;

    const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyName,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const [cart, setCart] = useState<ICart>();

    const [initTotalsCalculated, setInitTotalsCalculated] = useState(false);
    const [stripeLoaded, setStripeLoaded] = useState(false);
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
    const [paymentIntent, setPaymentIntent] = useState<IOnCreateCartPaymentIntentDTO>();
    const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
    const [isCartValid, setIsCartValid] = useState<boolean>(false);
    const [placeOrderActive, setPlaceOrderActive] = useState<boolean>(false);
    const [cartUpdating, setCartUpdating] = useState<boolean>(false);
    const [paymentForm, setPaymentForm] = useState<HTMLFormElement | null>(null);
    const [stripe, setStripe] = useState<Stripe | null>();
    const [elements, setElements] = useState<StripeElements | null>();
    const [setupInventoryOn, setSetupInventoryOn] = useState(false);
    const [updatingItemGuid, setUpdatingItemGuid] = useState("");
    const [cartUpdated, setCartUpdated] = useState(false);
    // Kit builder line currently open in the edit modal, if any.
    const [editingKitItem, setEditingKitItem] = useState<ICart_Item>();
    const [promoApplying, setPromoApplying] = useState(false);
    const [promoErrorKey, setPromoErrorKey] = useState<string | null>(null);

    const isAddressValid = useMemo(() => {
        // Local Pickup collects the order at the supplier, so the customer ship-to is not required
        // (the ship-to form is hidden and replaced by the supplier's pickup address).
        if (cart?.is_local_pickup) {
            return true;
        }

        return SupplierService.isAddressValidForCheckout(supplierModel, cart?.Ship_To);
    }, [cart?.Ship_To, cart?.is_local_pickup, supplierModel]);

    // Supplier minimum order total. Cart totals are in the catalog currency, the same currency the minimum is
    // in, so the amounts use the checkout's currencyFormatter. Below the minimum no payment intent is created
    // (the API would reject it) and Place Order stays disabled.
    const minOrder = useMemo(() => MinimumOrderService.evaluateCart(supplierModel, cart), [cart, supplierModel]);
    const isBelowMinOrder = !!minOrder && !minOrder.met;

    // Totals signature of a cart the API rejected for the minimum. While the cart still matches it, the intent
    // effect does not retry, so a client / server disagreement cannot loop create -> reject -> refetch.
    const minOrderRejectedSignatureRef = useRef<string | null>(null);

    // Totals signature of the cart a createPaymentIntent request was sent for, until it answers. A rejection
    // records this one, not the latest cart: the customer may have raised the total while it was in flight.
    // (The socket listeners are bound once on mount, so `notify` reads it through the ref.)
    const intentRequestSignatureRef = useRef<string | null>(null);

    const cartTotalsSignature = (fromCart: ICart | undefined): string => {
        return [fromCart?.sub_total, fromCart?.discount_total, fromCart?.promo_discount_total, fromCart?.Cart_Items?.length].join("|");
    };

    // Fulfillment choice (Ship / Local Pickup / Local Delivery). The cart carries the two order flags
    // (is_local_pickup / is_local_delivery) so the server can stamp them onto the order, including on
    // the Stripe webhook path that has no client payload. Both false = Ship; null = not chosen yet.
    type FulfillmentChoice = "shipping" | "local_pickup" | "local_delivery";

    const showFulfillment = !!(supplierModel.has_local_pickup || supplierModel.has_local_delivery);

    const fulfillmentChoice: FulfillmentChoice = cart?.is_local_pickup
        ? "local_pickup"
        : cart?.is_local_delivery
          ? "local_delivery"
          : "shipping";

    const fulfillmentOptions: IOwRadioSelectOption[] = [
        { value: "shipping", label: "shipping" },
        ...(supplierModel.has_local_pickup ? [{ value: "local_pickup", label: "local_pickup" }] : []),
        ...(supplierModel.has_local_delivery ? [{ value: "local_delivery", label: "local_delivery" }] : []),
    ];

    const fulfillmentFlags = (choice: FulfillmentChoice) => ({
        is_local_pickup: choice === "local_pickup",
        is_local_delivery: choice === "local_delivery",
    });

    // Flags to send with a checkout payload. Only sent when the supplier offers the choice, and
    // always both together so the server can tell an explicit "Ship" from "not mentioned".
    const fulfillmentPayload = (fromCart: ICart | undefined) => {
        if (!showFulfillment || !fromCart) {
            return {};
        }

        return fulfillmentFlags(fromCart.is_local_pickup ? "local_pickup" : fromCart.is_local_delivery ? "local_delivery" : "shipping");
    };

    // Seed a never-chosen cart (both flags null) from the vendor's default when the supplier allows
    // it. The seeded choice is persisted the first time a payload carries it (intent create for
    // credit-card users, checkout submit for net-term users).
    const applyVendorDefaultFulfillment = (fromCart: ICart): ICart => {
        const notChosen =
            (fromCart.is_local_pickup === null || fromCart.is_local_pickup === undefined) &&
            (fromCart.is_local_delivery === null || fromCart.is_local_delivery === undefined);

        if (!showFulfillment || !notChosen) {
            return fromCart;
        }

        if (vendorModel.default_local_pickup && supplierModel.has_local_pickup) {
            return { ...fromCart, ...fulfillmentFlags("local_pickup") };
        }

        if (vendorModel.default_local_delivery && supplierModel.has_local_delivery) {
            return { ...fromCart, ...fulfillmentFlags("local_delivery") };
        }

        return fromCart;
    };

    // Set when a fulfillment change was pushed through the payment intent so the cart (and its
    // recalculated shipping) is re-fetched once the intent update comes back.
    const refreshCartAfterIntentRef = useRef(false);

    const refreshOnValidateInventory = () => {
        socket.removeListener(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
        socket.on(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
    };

    const refreshOnCompleteCheckout = () => {
        socket.removeListener(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
        socket.on(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
    };

    const createPaymentIntent = () => {
        if (payWithStripe) {
            const payload: ICreateCartPaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            if (cart?.qbd_ship_via?.ListID) {
                payload.qbd_ship_via = cart.qbd_ship_via;
            }

            Object.assign(payload, fulfillmentPayload(cart));

            const utm = getCapturedUtm();
            if (utm) {
                payload.utm = utm;
            }

            intentRequestSignatureRef.current = cartTotalsSignature(cart);
            socket.emit(EndpointsEnum.CartCreatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const onCreatePaymentIntent = (data: IOnCreateCartPaymentIntentDTO) => {
        intentRequestSignatureRef.current = null;
        setPaymentIntent(data);
        setPaymentIntentLoading(false);
    };

    const updatePaymentIntent = () => {
        if (payWithStripe && paymentIntent) {
            const payload: IUpdatePaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                payment_intent_id: paymentIntent.paymentIntentId,
            };

            if (cart?.qbd_ship_via?.ListID) {
                payload.qbd_ship_via = cart.qbd_ship_via;
            }

            Object.assign(payload, fulfillmentPayload(cart));

            socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const onUpdatePaymentIntent = (data: IOnUpdatePaymentIntentDTO) => {
        setPaymentIntent(data);
        setPaymentIntentLoading(false);

        if (refreshCartAfterIntentRef.current) {
            refreshCartAfterIntentRef.current = false;
            getCart();
        }
    };

    const onCalculateTotals = (data: ICartOnCalculateTotalsDTO) => {
        setInitTotalsCalculated(true);
        setCart(applyVendorDefaultFulfillment(data.cart));
    };

    const notify = (data?: IErrorDTO) => {
        setPlaceOrderActive(false);
        // Any Notify while a promo apply / remove is in flight means it failed (the API's fallback
        // codes ERROR_APPLYING_PROMO_CODE / ERROR_REMOVING_PROMO_CODE lack the ERROR_PROMO_CODE_
        // prefix), so always release the busy state.
        setPromoApplying(false);

        // A carried promo code stopped validating at a checkout choke point (createPaymentIntent /
        // completeCheckout). The API already cleared it from the cart, so reload the cart, show the
        // reason inline, and drop the cached intent so the next cart change mints a fresh one against
        // the corrected total (see the createPaymentIntent effect below).
        if (data && PromoCodeService.isPromoCodeNotifyMessage(data.message)) {
            setPaymentIntentLoading(false);
            setPromoErrorKey(data.message.toLowerCase());
            setPaymentIntent(undefined);
            getCart();
        }

        // Below the supplier's minimum order total (createPaymentIntent / updatePaymentIntent /
        // completeCheckout). The API already cancelled any attached intent; reload the cart so the notice
        // shows the current shortfall. A rejected create remembers the cart it was sent for, so the intent
        // effect doesn't retry that same cart (only a create can loop; update / complete don't re-fire it).
        if (data && MinimumOrderService.isMinOrderTotalNotifyMessage(data.message)) {
            setPaymentIntentLoading(false);
            setPaymentIntent(undefined);

            if (intentRequestSignatureRef.current) {
                minOrderRejectedSignatureRef.current = intentRequestSignatureRef.current;
                intentRequestSignatureRef.current = null;
            }

            getCart();
        }
    };

    const applyCartPromoCode = (code: string) => {
        const payload: ICartApplyPromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            cartGuid: cart?.guid,
            code,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.CartApplyPromoCode, payload);
    };

    const removeCartPromoCode = () => {
        const payload: ICartRemovePromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            cartGuid: cart?.guid,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.CartRemovePromoCode, payload);
    };

    /**
     * Apply / remove result. An invalid code is a normal result (`validation.valid === false`) that
     * leaves the cart untouched. On success the API has cancelled any attached Stripe intent, so the
     * cached intent is dropped and the cart effect creates a new one for the discounted amount.
     */
    const onCartPromoCodeResult = (data: ICartOnApplyPromoCodeDTO) => {
        setPromoApplying(false);

        if (data.validation && data.validation.valid === false) {
            setPromoErrorKey(PromoCodeService.getErrorMessageKey(data.validation.reason));
            return;
        }

        setPromoErrorKey(null);
        setPaymentIntent(undefined);
        setCart(applyVendorDefaultFulfillment(data.cart));
    };

    const onGetCart = (data: IOnGetCartDTO) => {
        setCart(applyVendorDefaultFulfillment(data.cart));
    };

    useEffect(() => {
        if (!initTotalsCalculated && cart) {
            const payload: ICartCalculateTotalsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                cartGuid: cart.guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartCalculateTotals, payload);
        }

        if (!setupInventoryOn && cart) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [cart]);

    useEffect(() => {
        if (cart && initTotalsCalculated) {
            const rejectedForMinOrder = minOrderRejectedSignatureRef.current === cartTotalsSignature(cart);

            if (!paymentIntent && cart?.Cart_Items?.length && !isBelowMinOrder && !rejectedForMinOrder) {
                createPaymentIntent();
            }
        }
    }, [cart, initTotalsCalculated]);

    const onUpdateShipTo = (data: IOnUpdateCartShipToDTO) => {
        getCart();
        setCartUpdated(true);
    };

    useEffect(() => {
        if (cartUpdated) {
            updatePaymentIntent();
            setCartUpdated(false);
        }
    }, [cartUpdated]);

    const onUpdateItem = () => {
        setUpdatingItemGuid("");
        setCartUpdated(true);

        getCart();
    };

    const onDeleteItem = (data: IOnDeleteCartItemDTO) => {
        getCart();
        setCartUpdated(true);
    };

    const onUpdateProp = (data: IOnUpdateCartPropDTO) => {
        const notification: INotification = {
            message: "successfully_updated_cart",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        getCart();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CartOnCreatePaymentIntent, fn: onCreatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnUpdatePaymentIntent, fn: onUpdatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnUpdateShipTo, fn: onUpdateShipTo },
        { endpoint: EndpointsEnum.Notify, fn: notify },
        { endpoint: EndpointsEnum.CartOnDeleteItem, fn: onDeleteItem },
        { endpoint: EndpointsEnum.CartOnUpdateItem, fn: onUpdateItem },
        { endpoint: EndpointsEnum.CartOnCalculateTotals, fn: onCalculateTotals },
        { endpoint: EndpointsEnum.CartOnUpdateProp, fn: onUpdateProp },
        { endpoint: EndpointsEnum.CartOnApplyPromoCode, fn: onCartPromoCodeResult },
        { endpoint: EndpointsEnum.CartOnRemovePromoCode, fn: onCartPromoCodeResult },
    ];

    useEffect(() => {
        if (!stripeLoaded) {
            if (payWithStripe && supplierModel.Supplier_Integration.stripe_publishable_api_key) {
                loadStripe(supplierModel.Supplier_Integration.stripe_publishable_api_key).then((_stripe) => {
                    setStripePromise(_stripe);
                });
            }

            setStripeLoaded(true);
        }

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getCart();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }

            const listeners = socket.listeners(EndpointsEnum.CartOnValidateInventory);

            // to be sure we clean up all the listeners that are CartOnValidateInventory and CartOnCompleteCheckout
            // we get into a weird state sometimes, no idea why yet.
            socket.removeAllListeners(EndpointsEnum.CartOnValidateInventory);
            socket.removeAllListeners(EndpointsEnum.CartOnCompleteCheckout);
        };
    }, []);

    useEffect(() => {
        if (!setupInventoryOn && stripe) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [stripe]);

    const getCart = async () => {
        try {
            const response = await CartService.getCart(userModel, supplierModel, vendorModel);
            onGetCart(response);
        } catch (error) {
            console.error("Failed to get cart:", error);
        }
    };

    const validateInventory = () => {
        refreshOnValidateInventory();

        const payload: ICartValidateInventoryDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
        };

        socket.emit(EndpointsEnum.CartValidateInventory, payload);

        // console.log("validateInventory");

        setPlaceOrderActive(true);
    };

    const onValidateInventory = (data: ICartOnValidateInventoryDTO) => {
        // console.log("onValidateInventory");
        // Unavailable items block both paths: the API does not re-check inventory when it creates the order.
        if (data.cartItems?.length) {
            setPlaceOrderActive(false);
            return;
        }

        if (!payWithStripe) {
            // console.log("onValidateInventory no stripe");
            refreshOnCompleteCheckout();

            const payload: ICartCompleteCheckoutDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            if (cart?.note) {
                payload.note = cart.note;
            }

            if (cart?.po_no) {
                payload.po_no = cart.po_no;
            }

            if (cart?.qbd_ship_via?.ListID) {
                payload.qbd_ship_via = cart.qbd_ship_via;
            }

            Object.assign(payload, fulfillmentPayload(cart));

            const utm = getCapturedUtm();
            if (utm) {
                payload.utm = utm;
            }

            socket.emit(EndpointsEnum.CartCompleteCheckout, payload);
        } else {
            // console.log("onValidateInventory else");
            // process the order as normal
            // form={"paymentForm"} type={"submit"} // how to manually submit the form data?
            // @ts-expect-error
            const paymentFormElem = document.forms.paymentForm;

            if (paymentFormElem) {
                handleSubmit();
            }
        }
    };

    const onCompleteCheckout = (data: ICartOnCompleteCheckoutDTO) => {
        // console.log("onCompleteCheckout");
        if (cart) {
            const url = `/ecomm/checkout/complete?g=${cart.guid}`;

            history(url);
        }
    };

    const pollForInactiveStatusAndRedirect = async (cartGuid: string) => {
        const POLL_MAX_ATTEMPTS = 6;
        const POLL_INTERVAL_MS = 1000;
        const inactiveStatuses: CartStatusEnum[] = [CartStatusEnum.FAILED, CartStatusEnum.PAYMENT_ERRORED, CartStatusEnum.PAYMENT_PENDING];

        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
            try {
                const statusResp = await CartService.getCartStatus(
                    cartGuid,
                    userModel,
                    supplierModel,
                    userModel.isSupplierUser ? undefined : vendorModel
                );

                if (inactiveStatuses.includes(statusResp.status as CartStatusEnum)) {
                    refreshInactiveCarts();
                    history(`/ecomm/inactive-cart/${cartGuid}`);
                    return;
                }
            } catch (err) {
                console.warn("Failed to refresh cart status after Stripe decline", err);
            }

            if (attempt < POLL_MAX_ATTEMPTS - 1) {
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            }
        }
    };

    const handleSubmit = async () => {
        // event.preventDefault();

        if (!stripe || !elements || !cart) {
            return;
        }

        // first before submitting we need to validate the inventory in the cart.

        const apiUrl = SocketService.getApiEndpointURl();

        let result;
        try {
            result = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${apiUrl}/stripe-complete-checkout/${cart.guid}`,
                },
                redirect: "if_required",
            });
        } catch (err) {
            console.warn("stripe.confirmPayment threw", err);
            setPlaceOrderActive(false);

            const message = err instanceof Error && err.message ? err.message : "pending_cart_detail_errors_retry";
            const isFormatted = err instanceof Error && !!err.message;
            const notification: INotification = {
                message,
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification, isFormatted);

            void pollForInactiveStatusAndRedirect(cart.guid);
            return;
        }

        if (result.error) {
            // Restore the button right away so the UI isn't stuck behind the
            // status poll. The poll runs in the background and redirects the
            // user to the recovery page if the backend flips the cart status
            // within the poll window.
            setPlaceOrderActive(false);

            const hasStripeMessage = !!result.error.message;
            const notification: INotification = {
                message: hasStripeMessage ? result.error.message! : "pending_cart_detail_errors_retry",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification, hasStripeMessage);

            // Stripe's webhook is what flips the cart to FAILED / PAYMENT_ERRORED
            // on the backend, and that arrives slightly after confirmPayment
            // resolves. Poll briefly so we can redirect to the inactive-cart
            // recovery page as soon as the status lands.
            const cartGuid = cart.guid;
            void pollForInactiveStatusAndRedirect(cartGuid);
            return;
        }

        // Success path with `redirect: 'if_required'`: Stripe didn't redirect
        // because no 3DS / wallet step was needed. Tell the backend to confirm
        // the payment intent and land the user on the complete page.
        const paymentIntent = "paymentIntent" in result ? result.paymentIntent : undefined;
        if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
            try {
                await CartService.confirmCartPayment(
                    cart.guid,
                    userModel,
                    supplierModel,
                    userModel.isSupplierUser ? undefined : vendorModel
                );
            } catch (err) {
                console.warn("Failed to confirm cart payment after successful Stripe intent", err);
            }
            history(`/ecomm/checkout/complete?g=${cart.guid}`);
            return;
        }

        // Anything else (unexpected status, no paymentIntent) — restore the
        // button so the user can retry.
        setPlaceOrderActive(false);
    };

    const setPaymentFormRef = (formRef: HTMLFormElement | null) => {
        setPaymentForm(formRef);
    };

    const setElementsFn = (_elements: StripeElements | null) => {
        setElements(_elements);
    };

    const setStripeFn = (_stripe: Stripe | null) => {
        setStripe(_stripe);
    };

    const updateAddlInput = (cartItem: ICart_Item, addlInput: string) => {
        const payload: IUpdateCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            addlInput,
            cartItemGuid: cartItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartUpdateItem, payload);

        setCartUpdating(true);
        setUpdatingItemGuid(cartItem.guid);
    };

    const updateQty = (cartItem: ICart_Item, qty: number) => {
        if (cartItem.qty !== qty) {
            const payload: IUpdateCartItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                qty,
                cartItemGuid: cartItem.guid,
                uomSetId: cartItem.uom_set_id,
                recalculateTotals: true,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartUpdateItem, payload);

            setCartUpdating(true);
            setUpdatingItemGuid(cartItem.guid);
        }
    };

    const deleteItem = (cartItem: ICart_Item) => {
        const payload: IDeleteCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartItemGuid: cartItem.guid,
            recalculateTotals: true,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartDeleteItem, payload);

        setCartUpdating(true);
    };

    const updateNote = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.note = e.target.value;

            setCart(updatedCart);
        }
    };

    const updatePo = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.po_no = e.target.value;

            setCart(updatedCart);
        }
    };

    const updateShipVia = (selected: { value: string; title: string }) => {
        const option = (supplierModel.Supplier_Integration?.qbd_ShipMethodOptions || [])
            .filter((item) => item.IsActive)
            .find((item) => item.ListID === selected?.value);

        if (cart) {
            setCart({
                ...cart,
                qbd_ship_via: option || null,
            });
        }

        // Keep the credit-card payment intent in sync so the cart carries the selected ship method.
        if (payWithStripe && paymentIntent && option?.ListID) {
            const payload: IUpdatePaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                payment_intent_id: paymentIntent.paymentIntentId,
                qbd_ship_via: option,
            };

            socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const updateFulfillment = (e: ChangeEvent<any>) => {
        const choice = e.target.value as FulfillmentChoice;
        const flags = fulfillmentFlags(choice);

        if (!cart) {
            return;
        }

        const updatedCart: ICart = { ...cart, ...flags };

        setCart(updatedCart);

        if (payWithStripe) {
            // Credit card: persist through the payment intent (like ship via) so the intent amount is
            // refreshed with the recalculated shipping, then re-fetch the cart to show it. Without an
            // intent yet, the flags ride along on the intent create.
            if (paymentIntent) {
                const payload: IUpdatePaymentIntentDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    vendorGuid: vendorModel.guid,
                    payment_intent_id: paymentIntent.paymentIntentId,
                    ...flags,
                };

                if (cart.qbd_ship_via?.ListID) {
                    payload.qbd_ship_via = cart.qbd_ship_via;
                }

                refreshCartAfterIntentRef.current = true;

                socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

                setPaymentIntentLoading(true);
            }
        } else {
            // Net terms: persist on the cart now so the server recalculates shipping and the summary
            // updates. po_no / note ride along so the re-fetched cart keeps what has been typed.
            const payload: IUpdateCartPropDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                po_no: cart.po_no,
                note: cart.note,
                ...flags,
            };

            socket.emit(EndpointsEnum.CartUpdateProp, payload);
        }
    };

    const saveCartMetaData = async () => {
        if (cart) {
            const payload: IUpdateCartPropDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                po_no: cart.po_no,
                note: cart.note,
                ...fulfillmentPayload(cart),
            };

            socket.emit(EndpointsEnum.CartUpdateProp, payload);
        }
    };

    const goBack = () => {
        history(-1);
    };

    return (
        <ECommerceCheckoutContainerDiv className={"container-fluid container-md"}>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                <OwButton fn={goBack} leftIconClass="fas fa-arrow-left" className="btn-link p-0 me-3" ariaLabel="Go back" title={"back"} />
            </div>
            <div className="d-flex align-items-center">
                <OwTitle title={"checkout"} className="mb-0" isBold={true} />
            </div>

            <ECommerceInsideContainer className={"d-flex flex-column flex-md-row flex-grow-1 flex-md-grow-0 row"}>
                <ECommerceMainDetailsDiv className={"col-md-8 col-lg-6 col-xl-6"}>
                    <ECommerceShippingAddressContainerDiv className={"flex-column"}>
                        <DeliveryDeadlineInfo
                            schedule={cart?.Vendor?.Delivery_Schedule}
                            timezone={cart?.Vendor?.timezone || supplierModel?.timezone}
                        />
                        <div className="">
                            <OwTitle
                                title={cart?.is_local_pickup ? "1_pickup_address" : "1_shipping_address"}
                                tag={TitleTagEnum.H5}
                                isUnderline={true}
                            />
                        </div>
                        <div className="w-100">
                            {cart && cart.is_local_pickup ? (
                                // Local Pickup: the order is collected at the supplier, so show where to pick
                                // it up instead of the customer ship-to form.
                                <AddressDisplay
                                    address={supplierModel.Ship_To}
                                    showName={true}
                                    showCompanyName={true}
                                    showPhone={true}
                                    hideFax={true}
                                    hideMobile={true}
                                    hideEmail={true}
                                />
                            ) : cart ? (
                                <EcommerceCheckoutShipTo
                                    cart={cart}
                                    socket={socket}
                                    notifyFn={uiContext.notifyFn}
                                    setActivityIndicator={uiContext.setActivityIndicator}
                                />
                            ) : null}
                        </div>
                    </ECommerceShippingAddressContainerDiv>

                    {payWithStripe ? (
                        <ECommercePaymentMethodContainerDiv>
                            <div className="">
                                <OwTitle title={"2_payment_method"} tag={TitleTagEnum.H5} isUnderline={true} />
                            </div>
                            <div className="w-100">
                                {paymentIntent && cart ? (
                                    <Elements stripe={stripePromise} options={paymentIntent as any}>
                                        <EcommerceStripeCardForm
                                            socket={socket}
                                            cart={cart}
                                            isCartValid={isCartValid}
                                            setPaymentFormRef={setPaymentFormRef}
                                            setElements={setElementsFn}
                                            setStripe={setStripeFn}
                                        />
                                    </Elements>
                                ) : (
                                    <div>
                                        <ActivityIndicator active={true} /> <OwLabel title={"loading"} />
                                    </div>
                                )}
                            </div>
                        </ECommercePaymentMethodContainerDiv>
                    ) : null}

                    <ECommerceReviewItemsContainerDiv className={"flex-column"}>
                        <div className="">
                            {payWithStripe ? (
                                <OwTitle title={"3_review_items"} tag={TitleTagEnum.H5} isUnderline={true} />
                            ) : (
                                <OwTitle title={"2_review_items"} tag={TitleTagEnum.H5} isUnderline={true} />
                            )}
                        </div>
                        <div className=" flex-grow-1 flex-column">
                            {cart?.Cart_Items?.filter((cartItem) => !cartItem.main_kit_cart_item_id).map((cartItem) => {
                                let kitBuilderItems: ICart_Item[] | undefined = [];

                                if (cartItem.is_kit_builder) {
                                    kitBuilderItems = cart?.Cart_Items?.filter((kitCartItem) => {
                                        return kitCartItem.main_kit_cart_item_id === cartItem.id;
                                    });
                                }

                                return (
                                    <EcommerceCheckoutCartItem
                                        key={cartItem.guid}
                                        cartItem={cartItem}
                                        priceGroup={vendorModel.Price_Group}
                                        updateQty={updateQty}
                                        deleteItem={deleteItem}
                                        isUpdating={cartItem.guid === updatingItemGuid}
                                        updateAddlInput={updateAddlInput}
                                        kitBuilderItems={kitBuilderItems}
                                        editKit={setEditingKitItem}
                                    />
                                );
                            })}
                            {editingKitItem && cart?.Cart_Items ? (
                                <EcommerceCartKitEditor
                                    socket={socket}
                                    cartItem={editingKitItem}
                                    cartItems={cart.Cart_Items}
                                    recalculateTotals={true}
                                    onSave={() => {
                                        setCartUpdating(true);
                                        setUpdatingItemGuid(editingKitItem.guid);
                                    }}
                                    onClose={() => setEditingKitItem(undefined)}
                                />
                            ) : null}
                        </div>
                    </ECommerceReviewItemsContainerDiv>
                </ECommerceMainDetailsDiv>
                <EcommerceRightSideContainer className={"col-md-4 col-lg-3 flex-grow-1 flex-md-grow-0 flex-column-reverse flex-sm-column"}>
                    <ECommerceFloatingSummaryDiv className={"mt-4 mt-md-0 mb-2 mb-md-0"}>
                        {!isAddressValid && (
                            <div className="alert alert-warning mb-2">
                                <div className="text-center">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                </div>
                                <div>
                                    <OwLabel title={"address_required_for_checkout"} />
                                </div>
                            </div>
                        )}
                        {isBelowMinOrder && minOrder ? (
                            <div className="alert alert-warning mb-2">
                                <div className="text-center">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                </div>
                                <div>
                                    <MinOrderTotalNotice
                                        amounts={{
                                            minimum: currencyFormatter.format(minOrder.minimum),
                                            shortfall: currencyFormatter.format(minOrder.shortfall),
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}
                        <div>
                            <OwButton
                                title={"place_your_order"}
                                className={"btn-warning"}
                                btnSizeClass="btn-lg"
                                fn={validateInventory}
                                active={placeOrderActive}
                                disableOnActive={true}
                                activeTitle={"please_wait"}
                                disabled={
                                    (!paymentIntent && payWithStripe) ||
                                    !cart?.Cart_Items?.length ||
                                    cartUpdated ||
                                    (!!supplierModel.orders_require_po && !cart?.po_no?.length) ||
                                    paymentIntentLoading ||
                                    !isAddressValid ||
                                    isBelowMinOrder
                                }
                            />
                        </div>
                        {!hidePricing ? (
                            <div>
                                <hr />
                            </div>
                        ) : null}
                        {!hidePricing ? (
                            <div>
                                <div>
                                    <OwTitle title={"order_summary"} tag={TitleTagEnum.H5} />
                                </div>
                                <div className={"items"}>
                                    <OwLabel title={"items"} /> {currencyFormatter.format(cart?.sub_total || 0)}
                                </div>

                                {cart?.discount_total && cart?.discount_total > 0 ? (
                                    <div className={"discount"}>
                                        <OwLabel title={"discount"} /> -{currencyFormatter.format(cart?.discount_total || 0)}
                                    </div>
                                ) : null}

                                {supplierModel.has_promo_codes ? (
                                    <div className={"promo-code mt-2 mb-2"} data-testid={"checkout-promo-code"}>
                                        <OwPromoCodeInput
                                            appliedCode={cart?.promo_code}
                                            appliedDiscount={cart?.promo_discount_total}
                                            currency={currencyName}
                                            collapsible={true}
                                            rowLayout={true}
                                            onApply={applyCartPromoCode}
                                            onRemove={removeCartPromoCode}
                                            errorKey={promoErrorKey}
                                            applying={promoApplying}
                                            disabled={paymentIntentLoading || placeOrderActive || !cart?.Cart_Items?.length}
                                            pricingPrecision={2}
                                            isSmall={true}
                                        />
                                    </div>
                                ) : cart?.promo_code ? (
                                    <div className={"promo-code"}>
                                        <OwLabel title={"promo_code"} /> ({cart.promo_code}) -
                                        {currencyFormatter.format(cart.promo_discount_total || 0)}
                                    </div>
                                ) : null}

                                {vendorModel.tax_exempt ? null : (
                                    <div className={"tax"}>
                                        <OwLabel title={"tax"} /> {currencyFormatter.format(cart?.tax || 0)}
                                    </div>
                                )}

                                <div className={"shipping"}>
                                    <OwLabel title={"shipping"} /> {currencyFormatter.format(cart?.shipping || 0)}
                                </div>

                                <br />
                                <div className={"total"}>
                                    <OwLabel title={"order_total"} /> {currencyFormatter.format(cart?.grand_total || cart?.sub_total || 0)}
                                </div>
                            </div>
                        ) : null}
                    </ECommerceFloatingSummaryDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"po_no"} isBold={true} showIsReqAsterik={!!supplierModel.orders_require_po} />
                        </div>
                        <div>
                            <OwTextArea name={"po_no"} fn={updatePo} value={cart?.po_no || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"note"} isBold={true} />
                        </div>
                        <div>
                            <OwTextArea name={"note"} fn={updateNote} value={cart?.note || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    {showFulfillment ? (
                        <EcommerceNoteDiv>
                            <div>
                                <OwLabel title={"fulfillment"} isBold={true} />
                            </div>
                            <div>
                                <OwRadioSelect
                                    name={"fulfillment"}
                                    supplierModel={supplierModel}
                                    options={fulfillmentOptions}
                                    value={fulfillmentChoice}
                                    fn={updateFulfillment}
                                />
                            </div>
                        </EcommerceNoteDiv>
                    ) : null}
                    {supplierModel.Supplier_Integration?.has_qbd && supplierModel.Supplier_Integration?.qbd_show_ship_via_options ? (
                        <EcommerceNoteDiv>
                            <div>
                                <OwLabel title={"ship_via"} isBold={true} />
                            </div>
                            <div>
                                <OwSelectOption
                                    fn={updateShipVia}
                                    name={"qbd_ship_via"}
                                    titleProp={"title"}
                                    valueProp={"value"}
                                    options={(supplierModel.Supplier_Integration?.qbd_ShipMethodOptions || [])
                                        .filter((item) => item.IsActive)
                                        .map((item) => {
                                            return {
                                                value: item.ListID,
                                                title: item.Name,
                                            };
                                        })}
                                    emitObject={true}
                                    selectedOption={cart?.qbd_ship_via}
                                    selectedOptionProp={"ListID"}
                                    showBlankFirst={true}
                                />
                            </div>
                        </EcommerceNoteDiv>
                    ) : null}
                    {payWithStripe ? (
                        <div className={"mt-2"}>
                            <OwButton title={"save"} fn={saveCartMetaData} />
                        </div>
                    ) : null}
                </EcommerceRightSideContainer>
            </ECommerceInsideContainer>
        </ECommerceCheckoutContainerDiv>
    );
};

const ECommerceCheckoutContainerDiv = styled.div``;

const ECommerceInsideContainer = styled.div`
    display: flex;
`;

const ECommerceShippingAddressContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommercePaymentMethodContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommerceReviewItemsContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const EcommerceRightSideContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ECommerceFloatingSummaryDiv = styled.div`
    //min-width: 13rem;
    border: 1px solid var(--main-text-color);
    border-radius: 3px;
    padding: 0.5rem;
    height: fit-content;

    button {
        width: 100%;
    }

    hr {
        color: var(--main-text-color);
        border-color: var(--main-text-color);
    }

    .items,
    .discount,
    .tax,
    .shipping,
    .total {
        display: flex;
        justify-content: space-between;
    }
`;

const ECommerceMainDetailsDiv = styled.div``;

const EcommerceNoteDiv = styled.div`
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    height: auto;
`;
